import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { useMutation } from 'react-query';
import { InvoiceDetailModel, PaymentOrigin } from '@frontend/api-invoices';
import { JustfiIntegration1 } from '@frontend/api-justifi-integration';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { SetupFutureUsage } from '@frontend/api-weave-pay';
import { useCreatePaymentIntentSync, UseCreatePaymentIntentSyncOptions, useInvoicePerson } from '..';

interface UseCardOnFilePaymentJustifiProps {
  paymentMethod?: PaymentMethod;
  invoice?: InvoiceDetailModel;
  person?: Person;
  onInvoiceAlreadyPaid: UseCreatePaymentIntentSyncOptions['onInvoiceAlreadyPaid'];
  onRefetchInvoice: UseCreatePaymentIntentSyncOptions['onRefetchInvoice'];
}

export const useCardOnFilePaymentJustifi = ({
  paymentMethod,
  invoice,
  onInvoiceAlreadyPaid,
  onRefetchInvoice,
}: UseCardOnFilePaymentJustifiProps) => {
  const locationId = invoice?.locationId;
  const personId = invoice?.person.id;
  const pmId = invoice?.person.pmid;
  const { personEmail } = useInvoicePerson(invoice);
  const amount = invoice?.billedAmount ?? 0;

  const { createPaymentIntent /* paymentMethodId, confirmPaymentType */ } = useCreatePaymentIntentSync({
    paymentMethod,
    amount,
    email: personEmail,
    personId,
    locationId,
    origin: PaymentOrigin.ManualCardEntry,
    setupFutureUsage: SetupFutureUsage.onSession,
    invoiceId: invoice?.id,
    pmId,
    onInvoiceAlreadyPaid,
    onRefetchInvoice,
  });

  const { mutateAsync: mutateCreateCheckout } = JustfiIntegration1.Mutations.useCompleteCheckoutMutation();

  const { mutateAsync: makeCardOnFilePayment, isLoading: makingCardOnFilePayment } = useMutation({
    mutationFn: async () => {
      const { paymentIntent, paymentId } = await createPaymentIntent();
      if (!paymentIntent) {
        throw new Error('Payment intent not created');
      }
      const checkout = await mutateCreateCheckout({
        locationId,
        paymentMethodId: paymentMethod?.id,
        idempotentId: invoice?.id,
        checkoutId: paymentIntent.id,
      });

      return {
        workflowId: paymentId,
        paymentIntent,
        checkout,
      };
    },
    onError: (error) => {
      console.error('Error making card on file payment:', error);
    },
  });

  return { makeCardOnFilePayment, makingCardOnFilePayment };
};
